<template>
    <v-dialog v-if="showRevokeButton" v-model="revokeDialog" max-width="500" persistent>
        <template v-slot:activator="{ on: dialog }">
            <template v-if="activatorType === 'listItem'">
                <v-list-item v-if="canRevokeAction()" v-on="dialog">
                    <v-list-item-icon>
                        <v-icon color="secondary">clear</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="mr-5">Revoke user access</v-list-item-title>
                </v-list-item>
                <v-tooltip v-else left>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-list-item disabled v-on="tooltip">
                            <v-list-item-icon>
                                <v-icon>clear</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="mr-5">Revoke user access</v-list-item-title>
                        </v-list-item>
                    </template>
                    <span>You cannot revoke {{ cannotRevokeRoleLabel() }}.</span>
                </v-tooltip>
            </template>
            <v-tooltip v-else left>
                <template v-slot:activator="{ on: tooltip }">
                    <v-btn color="secondary" icon v-on="{ ...tooltip, ...dialog }" v-if="canRevokeAction()">
                        <v-icon>clear</v-icon>
                    </v-btn>
                    <v-tooltip v-else-if="hierarchicalLevel === nuvolosObjectTypes.INSTANCE && reason && reason.toUpperCase() !== 'EXPLICIT'" left>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon><v-icon>clear</v-icon></v-btn>
                        </template>
                        <span>You cannot revoke {{ cannotRevokeRoleLabel() }}.</span>
                    </v-tooltip>
                </template>
                <span>Revoke User Access</span>
            </v-tooltip>
        </template>
        <v-card>
            <v-card-title class="d-flex align-center flex-nowrap align-start">
                Revoke from {{ hierarchicalLevel }}
                <v-spacer></v-spacer>
                <v-btn :disabled="revokeLoading" @click="revokeDialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
                <v-card outlined class="mb-3">
                    <v-card-text>
                        <v-container fluid>
                            <v-row no-gutters class="mb-2">
                                <v-col>
                                    <label>User</label>
                                    <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ userName }}</v-sheet>
                                </v-col>
                            </v-row>
                            <v-row no-gutters v-if="hierarchicalLevel === nuvolosObjectTypes.INSTANCE" class="mb-2">
                                <v-col>
                                    <label>Instance</label>
                                    <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ instanceName }}</v-sheet>
                                </v-col>
                            </v-row>
                            <v-row no-gutters v-else-if="hierarchicalLevel === nuvolosObjectTypes.SPACE" class="mb-2">
                                <v-col>
                                    <label>Space</label>
                                    <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ spaceName }}</v-sheet>
                                </v-col>
                            </v-row>
                            <v-row no-gutters v-else-if="hierarchicalLevel === nuvolosObjectTypes.ORGANIZATION" class="mb-2">
                                <v-col>
                                    <label>Organization</label>
                                    <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ orgName }}</v-sheet>
                                </v-col>
                            </v-row>
                            <v-row no-gutters>
                                <v-col>
                                    <label>Role</label>
                                    <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ role }}</v-sheet>
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                </v-card>

                <v-alert text color="info">
                    <span v-if="hierarchicalLevel === nuvolosObjectTypes.INSTANCE">
                        By revoking the user instance role, the user will no longer be able to see or work with the instance.
                    </span>
                    <span v-if="hierarchicalLevel === nuvolosObjectTypes.SPACE">
                        By revoking the user space role, the user will lose the administrator rights for this space but will keep the instance-level roles if
                        any.
                    </span>
                    <span v-if="hierarchicalLevel === nuvolosObjectTypes.ORGANIZATION">
                        By revoking the user organization role, the user will no longer be able to see or work with any of the spaces and instances in the
                        organization.
                    </span>
                </v-alert>
                <v-checkbox v-if="hierarchicalLevel === nuvolosObjectTypes.SPACE" v-model="revokeSpaceChildren" hide-details color="error">
                    <template v-slot:label>
                        <span class="error--text">Revoke all instance roles for this user.</span>
                    </template>
                </v-checkbox>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn :loading="revokeLoading" color="error" text @click="confirmRevoke(ucid)">Revoke</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { enumsData } from '@/mixins/enums'

export default {
    name: 'UserActionRevokeDialog',
    mixins: [enumsData],
    props: {
        activatorType: {
            type: String,
            default: 'icon'
        },
        showRevokeButton: {
            type: Boolean,
            default: true
        },
        ucid: String,
        userName: String,
        hierarchicalLevel: String,
        role: String,
        reason: {
            type: String,
            default: null
        },
        spaceAdmin: {
            type: Boolean,
            default: null
        },
        instanceName: {
            type: String,
            default: null
        },
        instanceId: {
            type: String,
            default: null
        },
        spaceName: {
            type: String,
            default: null
        },
        orgName: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            revokeDialog: false,
            revokeLoading: false,
            revokeSpaceChildren: false
        }
    },
    methods: {
        canRevokeAction() {
            return (
                this.hierarchicalLevel === this.nuvolosObjectTypes.ORGANIZATION ||
                this.hierarchicalLevel === this.nuvolosObjectTypes.SPACE ||
                (this.reason && this.hierarchicalLevel === this.nuvolosObjectTypes.INSTANCE && this.reason.toUpperCase() === 'EXPLICIT') ||
                (this.hierarchicalLevel === this.nuvolosObjectTypes.INSTANCE && !this.spaceAdmin)
            )
        },
        cannotRevokeRoleLabel() {
            if (this.reason === this.roleTypes.SPACE_ADMIN) {
                return 'a space admin'
            }
            if (this.reason === this.roleTypes.ORG_MANAGER) {
                return 'an organization manager'
            }
            return this.reason ? `${this.reason} role` : 'this role'
        },
        confirmRevoke(userId) {
            this.revokeLoading = true
            let apiURL
            let isAsync = false

            if (this.hierarchicalLevel === this.nuvolosObjectTypes.ORGANIZATION) {
                apiURL = '/orgs/' + this.$route.params.oid + `/remove_conn/${userId}/async`
                isAsync = true
            } else if (this.hierarchicalLevel === this.nuvolosObjectTypes.SPACE && this.revokeSpaceChildren) {
                apiURL = '/spaces/' + this.$route.params.sid + `/remove_conn/${userId}`
            } else if (this.hierarchicalLevel === this.nuvolosObjectTypes.SPACE && !this.revokeSpaceChildren) {
                apiURL = '/spaces/' + this.$route.params.sid + `/revoke_role_from_conn/${userId}`
            } else if (this.hierarchicalLevel === this.nuvolosObjectTypes.INSTANCE) {
                apiURL = '/instances/' + this.instanceId + `/revoke_role_from_conn/${userId}`
            }

            this.$store.dispatch('showSnackBar', {
                snackBarText: isAsync ? 'Revoking access, see details on your tasks screen.' : 'Revoking access, please wait....',
                snackBarTimeout: 10000,
                snackBarIcon: 'info'
            })

            this.$axios
                .delete(apiURL)
                .then(() => {
                    !isAsync &&
                        this.$store.dispatch('showSnackBar', {
                            snackBarText: 'User access revoked successfully',
                            snackBarTimeout: 5000,
                            snackBarIcon: 'check_circle'
                        })
                    if (this.hierarchicalLevel === this.nuvolosObjectTypes.SPACE || this.hierarchicalLevel === this.nuvolosObjectTypes.INSTANCE) {
                        this.$store.dispatch('spaceStore/fetchSpaceUsers', this.$route.params.sid)
                    } else if (this.hierarchicalLevel === this.nuvolosObjectTypes.ORGANIZATION) {
                        this.$store.dispatch('orgStore/fetchOrgUsers', this.$route.params.oid)
                    }
                })
                .catch(() => {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText: 'An error has occurred',
                        snackBarTimeout: 10000,
                        snackBarIcon: 'error'
                    })
                })
                .finally(() => {
                    this.revokeLoading = false
                    this.revokeDialog = false
                })
        }
    }
}
</script>
